import { useState } from 'react';
import { applyPlaceholders, withSignature, smsCost } from '@aiinterview/shared';

/**
 * What a template will actually look like when it goes out.
 *
 * Every one of these templates is edited blind otherwise: an administrator types HTML into a
 * textarea, saves, and finds out whether it reads well when a candidate receives it. The
 * substitution here is the shared one the senders use, not a second implementation - a preview that
 * resolves differently from the sender is worse than no preview, because somebody tunes the wording
 * against it and ships the difference.
 *
 * The sample values are obviously sample values. A preview showing a real candidate's name would
 * invite somebody to check a name rather than a layout.
 */
/** Stands in for every credential-shaped placeholder below. Written once, used twice. */
const MASK = '•'.repeat(8);

export const SAMPLE_VALUES: Record<string, string> = {
    CandidateName: 'Priya Sharma',
    Position: 'Service Technician',
    CompanyName: 'Voltas Limited',
    ProductName: 'AI Interview',
    InterviewUrl: 'https://interviews.example.com/take/8f2c1a4e9b',
    InteractionUrl: 'https://interviews.example.com/interact/3d9e7c2f16',
    ExpiresAt: 'Fri, 28 Aug 2026 17:00:00 GMT',
    DurationMinutes: '20',
    QuestionCount: '10',
    SupportEmail: 'people.support@example.com',
    Remarks: 'Clear answers, strong product knowledge.',
    FormatName: 'Branch Pulse Check',
    Description: 'Five questions about how the last quarter went in your branch.',
    EmployeeCode: 'V10234',
    // Present so a messaging preview resolves the same placeholders the gateway will.
    mobile: '919876543210',
    link: 'https://interviews.example.com/interact/3d9e7c2f16',
    campaign: 'Branch Pulse Check',
    employeeCode: 'V10234',
    senderId: 'VOLTAS',
    username: 'your-username',
    // Never the real one. The mask exists so a preview of a URL shows the shape without ever
    // rendering a live credential onto a screen somebody might screenshot.
    password: MASK,
    apiKey: MASK
};

/**
 * Substitution without escaping, for a message rather than a document.
 *
 * The gateway adapter URL-encodes or JSON-escapes each value on the way out, but the network undoes
 * that before it reaches a handset - so what somebody reads is the plain substitution, and escaping
 * it here would show %3A%2F%2F in a preview of a perfectly good link.
 */
export const fillPlain = (template: string, values: Record<string, string>): string =>
    (template ?? '').replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
        const key = Object.keys(values).find((k) => k.toLowerCase() === name.toLowerCase());
        return key === undefined ? match : values[key]!;
    });

/**
 * The message a gateway request carries, wherever it keeps it.
 *
 * Every gateway puts the text somewhere different - a query parameter for the older ones, a JSON
 * field for the newer, a nested template parameter for Meta - so this looks in all three rather than
 * asking an administrator to tell us which. Returns every candidate it finds, because a Meta
 * template has one value per placeholder and all of them are worth seeing.
 */
export function messagesIn(url: string, body: string): string[] {
    const found: string[] = [];
    const named = /^(message|msg|text|sms|content|body)$/i;

    const query = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';

    if (query) {
        for (const [key, value] of new URLSearchParams(query)) {
            if (named.test(key) && value.trim() !== '') found.push(value);
        }
    }

    const walk = (node: unknown): void => {
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }

        if (node && typeof node === 'object') {
            for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
                if (typeof value === 'string' && named.test(key) && value.trim() !== '') {
                    found.push(value);
                } else {
                    walk(value);
                }
            }
        }
    };

    if (body.trim() !== '') {
        try {
            walk(JSON.parse(body));
        } catch {
            // A form-encoded body, or one mid-edit and not yet valid. Read it as a query string.
            for (const [key, value] of new URLSearchParams(body)) {
                if (named.test(key) && value.trim() !== '') found.push(value);
            }
        }
    }

    return found;
}

/** The approved template a Meta request names, so the preview can say which one it previews. */
export function metaTemplateName(body: string): string | null {
    try {
        const parsed: unknown = JSON.parse(body);
        const template = (parsed as { template?: { name?: unknown } } | null)?.template;
        return typeof template?.name === 'string' ? template.name : null;
    } catch {
        return null;
    }
}

/**
 * An email as its recipient sees it.
 *
 * Rendered inside an iframe rather than into the page. Email HTML carries its own inline styles and
 * frequently its own <style> block, and dropping that into the admin application lets a template
 * restyle the screen that is editing it. The iframe is sandboxed, so a pasted script cannot run
 * either.
 */
export function EmailPreview({ subject, bodyHtml, signatureHtml, values = SAMPLE_VALUES }: {
    subject?: string;
    bodyHtml: string;
    signatureHtml?: string;
    values?: Record<string, string>;
}) {
    const resolvedSubject = subject ? applyPlaceholders(subject, values) : null;
    const body = withSignature(
        applyPlaceholders(bodyHtml, values), signatureHtml ?? '', values);

    const document = `<!doctype html><html><head><meta charset="utf-8">`
        + `<meta name="viewport" content="width=device-width,initial-scale=1">`
        + `<style>body{margin:0;padding:16px;font-family:-apple-system,"Segoe UI",Roboto,`
        + `Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#1f2937;`
        + `background:#fff}img{max-width:100%}</style></head><body>${body}</body></html>`;

    return (
        <div className="overflow-hidden rounded-lg border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Subject</div>
                <div className="text-sm font-medium text-slate-800">
                    {resolvedSubject || <span className="text-slate-400">No subject set</span>}
                </div>
            </div>
            <iframe
                title="Email preview"
                sandbox=""
                srcDoc={document}
                className="h-80 w-full bg-white"
            />
        </div>
    );
}

/**
 * A text message or a WhatsApp message as a handset shows it.
 *
 * The character and segment counts are the reason this exists rather than a plain textarea echo: a
 * survey link is around fifty characters, which pushes most wordings past one segment, and operators
 * bill per segment. Finding that out from a preview costs nothing; finding it out from an invoice
 * after a campaign to four hundred people costs four hundred extra segments.
 */
export function MessagePreview({ text, channel, note }: {
    text: string;
    channel: 'Sms' | 'Whatsapp';
    note?: string;
}) {
    const cost = smsCost(text);
    const whatsapp = channel === 'Whatsapp';

    return (
        <div className="rounded-lg border border-slate-200 bg-slate-100 p-4">
            <div
                className={`max-w-sm rounded-2xl px-3.5 py-2.5 text-sm shadow-sm ${
                    whatsapp
                        ? 'rounded-tr-sm bg-[#dcf8c6] text-slate-800'
                        : 'rounded-tl-sm bg-white text-slate-800'
                }`}
            >
                {text.trim() === ''
                    ? <span className="text-slate-400">Nothing to send yet.</span>
                    : <span className="whitespace-pre-wrap break-words">{text}</span>}
            </div>

            {!whatsapp && text.trim() !== '' && (
                <p className="mt-3 text-xs text-slate-600">
                    <span className="font-medium">{cost.characters}</span> characters ·{' '}
                    <span className={cost.segments > 1 ? 'font-medium text-amber-700' : 'font-medium'}>
                        {cost.segments} segment{cost.segments === 1 ? '' : 's'}
                    </span>
                    {' · '}{cost.remaining} left before the next one
                    {cost.unicode && (
                        <span className="text-amber-700">
                            {' · '}non-Latin characters, so 70 per segment instead of 160
                        </span>
                    )}
                </p>
            )}

            {note && <p className="mt-3 text-xs text-slate-500">{note}</p>}
        </div>
    );
}

/** A preview that stays out of the way until asked for. */
export function PreviewToggle({ label = 'Preview', children }: {
    label?: string;
    children: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);

    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-xs font-medium text-[var(--brand-primary)] hover:underline"
            >
                {open ? `Hide ${label.toLowerCase()}` : label}
            </button>
            {open && <div className="mt-2">{children}</div>}
        </div>
    );
}
